import { useEffect, useState } from 'react';
import type { ShotOrientation } from '@/api/capture';
import { useT } from '@/i18n/runtime';

/** What the viewport currently is; null when it cannot be read (SSR / tests without a window). */
export function readOrientation(): 'LANDSCAPE' | 'PORTRAIT' | null {
  if (typeof window === 'undefined') return null;
  const so = window.screen?.orientation?.type;
  if (typeof so === 'string') return so.startsWith('landscape') ? 'LANDSCAPE' : 'PORTRAIT';
  if (!window.innerWidth || !window.innerHeight) return null;
  return window.innerWidth > window.innerHeight ? 'LANDSCAPE' : 'PORTRAIT';
}

/**
 * The orientation hint for a shot: the script's requirement in words, plus a live "rotate to
 * match" line when the phone is demonstrably the other way. Advisory — it never blocks the shutter
 * (the reviewer sees the frame; a wrong orientation is a quality note, not a refusal).
 */
export function OrientationHint({ required }: { required: ShotOrientation }): JSX.Element {
  const { t } = useT();
  const [current, setCurrent] = useState(readOrientation);
  useEffect(() => {
    const onChange = (): void => setCurrent(readOrientation());
    window.addEventListener('resize', onChange);
    window.addEventListener('orientationchange', onChange);
    return () => {
      window.removeEventListener('resize', onChange);
      window.removeEventListener('orientationchange', onChange);
    };
  }, []);
  const mismatch = required !== 'ANY' && current !== null && current !== required;
  return (
    <div className="sv-orientation" data-testid="orientation-hint" data-required={required} data-mismatch={mismatch ? 'true' : 'false'}>
      <span>{t(`sv.capture.orientation.${required}`)}</span>
      {mismatch ? <span className="sv-orientation-rotate">{t('sv.capture.orientation.rotate')}</span> : null}
    </div>
  );
}
